import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { RevealHeading, Reveal } from "../components/site/Reveal";
import { Medallion } from "../components/site/Ornaments";
import { SERVICES, whatsappLink } from "../data/site";

const Services = () => {
  const [hover, setHover] = useState(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const onMove = (e) => setPos({ x: e.clientX, y: e.clientY });

  return (
    <div data-testid="services-page" className="pt-32" onMouseMove={onMove}>
      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
        <div className="flex items-center gap-3 text-henna">
          <Medallion size={24} />
          <span className="text-xs uppercase tracking-[0.3em]">
            Services &amp; pricing
          </span>
        </div>
        <RevealHeading
          testId="services-title"
          className="mt-6 max-w-4xl font-display text-6xl leading-[0.9] tracking-tight sm:text-7xl lg:text-8xl"
          lines={[<>The</>, <span className="italic font-serif text-henna">offerings</span>]}
        />
        <Reveal delay={0.4}>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-ink/70">
            Transparent pricing, freehand designs. Final quote depends on
            intricacy, coverage and occasion. Hover a service to preview.
          </p>
        </Reveal>
      </section>

      {/* List */}
      <section className="mx-auto mt-16 max-w-[1400px] px-5 sm:px-8 lg:px-12">
        <div className="border-t border-ink/15">
          {SERVICES.map((s) => (
            <div
              key={s.id}
              data-testid={`service-row-${s.id}`}
              onMouseEnter={() => setHover(s)}
              onMouseLeave={() => setHover(null)}
              className="group border-b border-ink/15 py-10 transition-colors duration-500 hover:bg-cashmere/60"
            >
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-baseline gap-5">
                  <span className="text-xs text-gold">{s.index}</span>
                  <h2 className="font-display text-4xl leading-none tracking-tight transition-transform duration-500 group-hover:translate-x-3 sm:text-6xl">
                    {s.title}
                  </h2>
                </div>
                <span className="pl-10 font-serif text-2xl text-henna sm:pl-0 sm:text-3xl">
                  {s.price}
                </span>
              </div>
              <div className="mt-5 grid gap-6 pl-10 sm:grid-cols-12">
                <p className="max-w-md text-sm leading-relaxed text-ink/70 sm:col-span-7">
                  {s.desc}
                </p>
                <div className="flex flex-wrap gap-2 sm:col-span-5 sm:justify-end">
                  {s.tags.map((t) => (
                    <span
                      key={t}
                      className="rounded-full border border-ink/20 px-4 py-1.5 text-xs uppercase tracking-[0.12em] text-ink/60"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile preview images */}
        <div className="mt-12 grid grid-cols-2 gap-4 md:hidden">
          {SERVICES.map((s) => (
            <div key={s.id} className="aspect-[4/5] overflow-hidden">
              <img src={s.image} alt={s.title} className="img-cover" />
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-[1400px] px-5 py-24 sm:px-8 lg:px-12">
        <Reveal>
          <div className="flex flex-col items-start justify-between gap-8 border-t border-ink/15 pt-12 sm:flex-row sm:items-end">
            <h3 className="max-w-2xl font-serif text-4xl leading-tight tracking-tight sm:text-5xl">
              Not sure which suits your occasion? Let's{" "}
              <span className="italic text-henna">plan it together</span>.
            </h3>
            <a
              href={whatsappLink("Hi Arjun, I'd like to discuss mehndi for my event.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="services-enquire-btn"
              className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-henna px-8 py-4 text-xs uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-ink"
            >
              Enquire on WhatsApp
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </Reveal>
      </section>

      {/* Floating cursor image (desktop) */}
      <AnimatePresence>
        {hover && (
          <motion.div
            className="pointer-events-none fixed z-40 hidden h-64 w-52 overflow-hidden md:block"
            style={{ left: pos.x + 24, top: pos.y - 130 }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.35, ease: [0.19, 1, 0.22, 1] }}
          >
            <img src={hover.image} alt={hover.title} className="img-cover" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Services;
